/**
 * Local playground identity helpers.
 *
 * These are used for local Firebase Auth emulator mode
 * to allow multiple concurrent "players" across different browser instances.
 */

/** Regex matching legacy sequential player IDs: player1, player2, etc. */
const LEGACY_PLAYER_REGEX = /^player(\d+)$/

/** Regex matching auto-generated random player IDs: player_a8f3, player_00ff, etc. */
const RANDOM_PLAYER_REGEX = /^player_[0-9a-f]{4}$/

/**
 * Generate a random player ID with a 4-character hex suffix.
 * Produces IDs like "player_a8f3" (65,536 unique values).
 *
 * Used to auto-assign a unique identity per browser on first visit,
 * so two playground instances never silently share the same Firebase user.
 */
export function generateRandomPlayerId(): string {
  const hex = Math.random().toString(16).slice(2, 6).padEnd(4, '0')
  return `player_${hex}`
}

/**
 * Normalize a requested player id to the supported format.
 *
 * Accepted formats:
 * - "playerN" (N >= 1) — legacy sequential IDs from manual selection
 * - "player_XXXX" (4-char hex) — auto-generated random IDs
 * - Empty/invalid values generate a new random ID
 */
export function normalizeLocalPlaygroundPlayerId(value: unknown): string {
  if (typeof value !== 'string') return generateRandomPlayerId()
  const trimmed = value.trim().toLowerCase()
  if (trimmed.length === 0) return generateRandomPlayerId()

  // Accept legacy format: player1, player2, ...
  const legacyMatch = trimmed.match(LEGACY_PLAYER_REGEX)
  if (legacyMatch) {
    const n = Number(legacyMatch[1])
    if (Number.isFinite(n) && n >= 1) return `player${n}`
  }

  // Accept random format: player_a8f3, player_00ff, ...
  if (RANDOM_PLAYER_REGEX.test(trimmed)) {
    return trimmed
  }

  return generateRandomPlayerId()
}

/**
 * Build a deterministic email address for a local playground player id.
 */
export function buildLocalPlaygroundEmail(playerId: unknown): string {
  const normalized = normalizeLocalPlaygroundPlayerId(playerId)
  return `rundot-game-sandbox+${normalized}@local.test`
}
